import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { storage } from '../config/firebase';
import { ref, deleteObject, listAll } from 'firebase/storage';
import { useUser } from '@clerk/clerk-react';
import { motion } from 'framer-motion';

const LoadingPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const { user } = useUser();

  useEffect(() => {
    const fetchFileName = async () => {
      if (!user) return;
      
      try {
        // List files in user's directory
        const userFilesRef = ref(storage, `users/${user.id}`);
        const filesList = await listAll(userFilesRef);
        
        // Get the first file (assuming one file per user)
        if (filesList.items.length > 0) {
          const name = filesList.items[0].name;
          setFileName(name);
        }
      } catch (error) {
        console.error('Error fetching file name:', error);
      }
    };

    // Only fetch file name if we came from file upload
    const source = sessionStorage.getItem('dataSource');
    if (source === 'fileUpload') {
      fetchFileName();
    } else {
      // For Plaid connections, skip file check
      setFileName('Connected Bank Account');
    }
  }, [user]);

  const handleProgressComplete = () => {
    setIsLoading(false);
    // Clear the data source from session storage
    sessionStorage.removeItem('dataSource');
    // Navigate to the next page
    navigate('/loading-progress');
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!user || !fileName) return;

    try {
      const source = sessionStorage.getItem('dataSource');
      if (source === 'fileUpload') {
        // Delete file from Firebase Storage
        const fileRef = ref(storage, `users/${user.id}/${fileName}`);
        await deleteObject(fileRef);
      }
      
      // Close modal and navigate to upload page
      setShowDeleteConfirm(false);
      
      // Store deletion success message in sessionStorage
      sessionStorage.setItem('fileDeleted', 'true');
      
      // Navigate back to upload page
      navigate('/upload');
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  // Show loading indicator while determining source
  if (!fileName && isLoading) {
    return (
      <div className="min-h-screen bg-[#8364FF]/[0.07] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#9B86EA] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#8364FF]/[0.07]">
      <div className="h-screen flex flex-col px-4 sm:px-6 py-12">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mt-4"
          >
            <h1 className="text-[26px] leading-[120%] font-black text-purple-dark
                         min-[320px]:text-[28px]
                         min-[375px]:text-[30px]
                         min-[428px]:text-[34px]
                         min-[912px]:text-[40px]
                         lg:text-[42px]">
              Find out what's
              <br />
              in your report
            </h1>
            <p className="text-[13px] leading-[150%] mt-2 text-purple-dark/60
                         min-[320px]:text-[14px]
                         min-[375px]:text-[15px]
                         min-[428px]:text-[16px]
                         min-[912px]:text-[18px]
                         lg:text-[20px]">
              {sessionStorage.getItem('dataSource') === 'fileUpload' 
                ? 'Processing your transaction history...'
                : 'Connecting to your bank account...'}
            </p>
          </motion.div>

          <div className="flex-1 flex items-center max-w-md mx-auto w-full">
            <div className="w-full max-w-[260px] min-[375px]:max-w-[280px] sm:max-w-sm bg-white rounded-xl 
                         p-2.5 min-[375px]:p-3 sm:p-4 shadow-brutal border-2 border-black mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs min-[375px]:text-sm font-medium truncate 
                              max-w-[160px] min-[375px]:max-w-[180px] sm:max-w-none">
                  {fileName}
                </span>
                <button 
                  onClick={handleDelete}
                  className="text-red-500 text-xs min-[375px]:text-sm hover:text-red-600 
                           transition-colors ml-2"
                >
                  Cancel
                </button>
              </div>
              <div className="h-1.5 min-[375px]:h-2 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#9B86EA]"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 2.5, ease: "easeInOut" }}
                  onAnimationComplete={handleProgressComplete}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center w-full mb-16">
            <button
              disabled={isLoading}
              onClick={() => navigate('/loading-progress')}
              className="w-full max-w-[260px] min-[375px]:max-w-[280px] sm:max-w-sm
                       bg-[#9B86EA] text-white font-bold 
                       py-2 min-[320px]:py-2.5 sm:py-3 px-6 sm:px-8 
                       text-base min-[320px]:text-lg rounded-[32px]
                       border-2 border-black shadow-brutal transition-all duration-200
                       active:translate-x-[4px] active:translate-y-[4px] active:shadow-none
                       disabled:opacity-50 disabled:cursor-not-allowed 
                       disabled:active:translate-x-0
                       disabled:active:translate-y-0 
                       disabled:active:shadow-brutal"
            >
              Next
            </button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-bold mb-2">Cancel Process?</h3>
              <p className="text-gray-600 mb-4">
                Are you sure you want to cancel? This will stop the current process.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  No, continue
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Yes, cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingPage;